import { Filtro, FiltroApoyoComplementario } from '@/shared/model/filtro.model';
import { Component, Emit, Inject } from 'vue-property-decorator';

import SolicitudService from '@/entities/solicitud/solicitud.service';

import { ISolicitud, Solicitud } from '@/shared/model/solicitud.model';
import { mixins } from 'vue-class-component';
import SolucionMixin from './solucion-mixin';
import Vue2Filters from 'vue2-filters';
import { Transicion } from '@/shared/model/proceso/transicion.model';
import { TransicionContext } from '@/shared/model/proceso/transicion-context.model';
import { TipoAccion } from '@/shared/model/enumerations/tipo-accion.model';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import { ISolicitudRevision } from '@/shared/model/solicitud-revision.model';
import { TipoSolucion } from '@/shared/model/enumerations/tipo-solucion.model';

interface ISolicitudDocumentos extends ISolicitud {
  documentos?: any[];
}

/**
 * An utility service for SolicitudMixin.
 */
@Component({
  mixins: [Vue2Filters.mixin],
})
export default class SolicitudMixin extends mixins(SolucionMixin) {
  @Inject('solicitudService') public solicitudService: () => SolicitudService;

  public idsSolicitud: Array<string> = [];

  public arraySolicitudes: Array<ISolicitud> = [];

  public solicitud: Solicitud;

  public errores: string[] = [];

  public reglasValidas = false;

  public getListaSolicitudes(listasolicitudes: Array<Solicitud>) {
    this.idsSolicitud = new Array<string>();
    for (const solicitudAsignada of listasolicitudes) {
      this.idsSolicitud.push(solicitudAsignada.id);
    }
    const filtroSolicitud = new Filtro();
    filtroSolicitud.idsSolicitud = this.idsSolicitud;
    filtroSolicitud.estadosSolicitud = [
      EstadoSolicitud.VALIDADA,
      EstadoSolicitud.DICTAMINADA,
      EstadoSolicitud.REVISADA,
      EstadoSolicitud.FORMALIZADA,
      EstadoSolicitud.ENVIADA,
      EstadoSolicitud.EN_CORRECCION,
    ];
    const paginationQuery = {
      page: SolucionMixin.PAGE - 1,
      size: listasolicitudes.length,
      sort: this.sort(),
      filter: filtroSolicitud,
    };
    if (listasolicitudes.length > 0) {
      this.solicitudService()
        .consultarSolicitudes(undefined, paginationQuery)
        .then(r => {
          this.arraySolicitudes = r.data;
        })
        .catch(err => {
          this.alertService().showHttpError(this, err.response);
        });
    }
  }

  /**
   * Actualiza una solicitud
   * @param solicitud Solicitud a actualizar
   */
  @Emit('solicitudDictaminada')
  public async dictaminarSolicitud(solicitud: Solicitud): Promise<Solicitud> {
    try {
      const transicionContext = new TransicionContext(new Transicion(TipoAccion.DICTAMINAR), null, solicitud.id, solicitud.solucionId);
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * La solicitud se aprueba por lo tanto la solicitud queda en estado revisado.
   * @param solicitud Solicitud a aprobar
   */
  public async aprobarSolicitud(solicitud: ISolicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.REVISAR);
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * La solicitud se envia a firmar el convenio.
   * @param solicitud Solicitud a firmar
   */
  public async enviaFirma(solicitud: ISolicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.ACTIVAR);
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Comprueba si la solicitud es de tipo ingreso.
   * @returns Devuelve true si la solicitud es de tipo ingreso, de lo contrario devuelve false.
   */
  public get solucionTipoIngreso(): boolean {
    return this.solicitud?.SOLICITUD_RIZOMA?.solucionSolicitud?.tipoSolucion === this.tipoIngreso;
  }

  /**
   * Comprueba si la solicitud es de tipo diaspora.
   * @returns Devuelve true si la solicitud es de tipo diaspora, de lo contrario devuelve false.
   */
  public get solucionTipoDiaspora(): boolean {
    return this.solicitud?.SOLICITUD_RIZOMA?.solucionSolicitud?.tipoSolucion === this.tipoDiaspora;
  }

  /**
   * Comprueba si la solicitud es de tipo emérito.
   * @returns Devuelve true si la solicitud es de tipo emérito, de lo contrario devuelve false.
   */
  public get solucionTipoEmerito(): boolean {
    return this.solicitud?.SOLICITUD_RIZOMA?.solucionSolicitud?.tipoSolucion === this.tipoEmerito;
  }

  /**
   * Ordena por comparación de cadenas, en otras palabras, ordena ascendentemente por el estado de la solicitud.
   * @param data Las solicitudes con revisiones
   */
  public ordenarPorEstado(data: ISolicitudRevision[]): ISolicitudRevision[] {
    return data.sort((a, b) => {
      return a.solicitud.estado.localeCompare(b.solicitud.estado);
    });
  }

  /**
   * Actualiza una solicitud a estado FORMALIZADA
   *
   * @param solicitud Solicitud a formalizar
   */
  public async formalizarSolicitud(solicitud: Solicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.FORMALIZAR);
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Actualiza una solicitud a estado PREFORMALIZADA
   *
   * @param solicitud Solicitud a preformalizar
   */
  public async preFormalizarSolicitud(solicitud: Solicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.PREFORMALIZAR);
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Actualiza una solicitud a estado BAJA
   *
   * @param solicitud Solicitud a preformalizar
   */
  public async bajaSolicitud(solicitud: Solicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.BAJAR);
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Actualiza una solicitud y agrega la variable 'expediente: true'
   * y se eliminan los documentos para que no se guarden en la solicitud
   *
   * @param solicitud Solicitud a actualizar.
   */

  public async actualizarEstadoSolicitud(solicitud: ISolicitudDocumentos): Promise<ISolicitudDocumentos> {
    const solicitudSinDocumentos = { ...solicitud, expediente: true };
    delete solicitudSinDocumentos.documentos;

    try {
      const solicitudActualizada = await this.solicitudService().partialUpdate(solicitudSinDocumentos);
      return solicitudActualizada;
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Actualiza una solicitud
   * y se eliminan los documentos para que no se guarden en la solicitud
   *
   * @param solicitud Solicitud a actualizar.
   */

  public async actualizaSolicitud(solicitud: ISolicitudDocumentos): Promise<ISolicitudDocumentos> {
    const solicitudSinDocumentos = { ...solicitud };
    delete solicitudSinDocumentos.documentos;

    try {
      const solicitudActualizada = await this.solicitudService().partialUpdate(solicitudSinDocumentos);
      return solicitudActualizada;
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Valida si cuenta con la uri de reconsideración
   */
  get isReconsideracion(): boolean {
    return !!this.solicitud?.reconsideracion?.uri;
  }

  /**
   * Actualiza una solicitud a estado VALIDADA
   *
   * @param solicitud Solicitud
   */
  public async regresarSolicitud(solicitud: Solicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.RECHAZAR);
    transicion.destino = EstadoSolicitud.VALIDADA;
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Actualiza una solicitud a estado FIRMADA
   *
   * @param solicitud Solicitud a preformalizar
   */
  public async solicitudEnFirma(solicitud: Solicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.CONFIRMAR);
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Obtiene la solicitud de Apoyo Complementario asociada a una beca específica.
   *
   * Construye un filtro de búsqueda para recuperar únicamente solicitudes:
   * - En estado FORMALIZADA
   * - Del tipo APOYOS_COMPLEMENTARIOS_BN
   * - Asociadas a la beca indicada
   * - Que contengan los tags proporcionados
   *
   * El resultado se obtiene de forma paginada y se almacena en `arraySolicitudes`.
   * En caso de error, se muestra el mensaje correspondiente mediante el servicio de alertas.
   *
   * @param becaid Identificador único de la beca.
   * @param tags Lista de etiquetas utilizadas para filtrar las solicitudes.
   *
   * @returns void
   */
  public async getSolicitudApoyoComplementario(becaid: string, tags?: Array<string>): Promise<Array<ISolicitud>> {
    const filtroSolicitud = new FiltroApoyoComplementario();
    filtroSolicitud.estadosSolicitud = [EstadoSolicitud.FORMALIZADA];
    filtroSolicitud.tipoSolucion = TipoSolucion.APOYOS_COMPLEMENTARIOS_BN;
    filtroSolicitud.becaId = becaid;
    filtroSolicitud.tags = tags;
    const paginationQuery = {
      page: SolucionMixin.PAGE - 1,
      size: 10,
      sort: this.sort(),
      filter: filtroSolicitud,
    };
    try {
      const response = await this.solicitudService().consultarSolicitudes(TipoSolucion.APOYOS_COMPLEMENTARIOS_BN, paginationQuery);
      return response.data;
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Actualiza una solicitud a estado FIRMADA
   *
   * @param solicitud Solicitud a enviar
   */
  public async solicitudEnviada(solicitud: Solicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.ENVIAR);
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Aplicar reglas en cuentas bancarias SNII
   *
   * @param solicitud Solicitud a validar
   */
  public async validaReglas(solicitud: Solicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;

    const transicion = new Transicion(TipoAccion.ACTIVAR);
    transicionContext.transicion = transicion;

    try {
      const response = await this.solicitudService().transition(transicionContext);

      this.reglasValidas = true;

      return response;
    } catch (error) {
      if (error?.response?.data?.errores) {
        this.avisos(error.response.data.errores);
      }

      this.reglasValidas = false;

      throw error;
    }
  }

  public avisos(errores: any[]) {
    this.errores = errores.map(error => error.condicion);
  }
}
